import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  ExpenseCategory,
  ExpenseResponse,
  ExpenseTotalResponse,
  RecordExpenseRequest,
  SalesSummaryResponse,
} from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { useChartsShown } from '../../components/charts/useChartsShown';
import { PageHeader } from '../../components/PageHeader';
import { PanelHeading } from './PanelHeading';
import { ErrorState } from '../../components/states/StateViews';
import { useAuthStore } from '../../auth/authStore';
import { useLocalisedName } from '../../i18n/localisedName';
import { clinicToday } from '../scheduling/clinicTime';
import { useBranchPicker } from '../scheduling/useBranchPicker';
import { money } from '../catalog/money';
import { ExpensesOverview } from '../insight/ExpensesOverview';

const CATEGORIES: readonly ExpenseCategory[] = [
  'CONSUMABLES',
  'MAINTENANCE',
  'RENT',
  'UTILITIES',
  'SALARIES',
  'MARKETING',
  'EQUIPMENT',
  'PROFESSIONAL_FEES',
  'OTHER',
];

/**
 * What a branch spent (FR-905, DEC-040).
 *
 * Expenses-lite by design: a category, a vendor, an amount. The totals sit above the list because
 * the question this screen is opened with is "what did this month cost", not "what was the third
 * thing we bought".
 *
 * Automatic entries from stock receipts and maintenance (M8) will arrive in the same list, marked
 * with their source and not editable here — an expense the system posted is not an expense a person
 * gets to revise.
 *
 * Above the totals, the same branch and dates as charts (CR-36): spent and waiting for approval,
 * spending day by day, by category and vendor, and — for whoever may read the takings — against
 * sales before VAT. They fold away.
 */
export function ExpensesPage() {
  const { t } = useTranslation();
  const { name } = useLocalisedName();
  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const permissions = useAuthStore((state) => state.permissions);
  const canRecord = permissions.has('EXPENSE_RECORD');
  const canApprove = permissions.has('EXPENSE_APPROVE');
  const canSeeSales = permissions.has('CASH_SUMMARY_READ');
  const [chartsShown, showCharts] = useChartsShown('expenses');

  // This month so far, by the clinic's calendar: a UTC date is yesterday's until 2 or 3 in the
  // morning in Cairo, and on the first of the month that is last month.
  const [from, setFrom] = useState(() => `${clinicToday().slice(0, 8)}01`);
  const [to, setTo] = useState(() => clinicToday());

  const range = `${branchId}:${from}:${to}`;
  const expenses = useApiQuery<ExpenseResponse[]>(
    queryKeys.expenses(clinicId ?? '', range),
    `/clinics/${clinicId}/expenses?branchId=${branchId}&from=${from}&to=${to}`,
    { enabled: Boolean(clinicId && branchId) },
  );

  const totals = useApiQuery<ExpenseTotalResponse[]>(
    queryKeys.expenseTotals(clinicId ?? '', range),
    `/clinics/${clinicId}/expenses/totals?branchId=${branchId}&from=${from}&to=${to}`,
    { enabled: Boolean(clinicId && branchId) },
  );

  const salesSearch = `from=${from}&to=${to}&branchId=${branchId}`;
  const sales = useApiQuery<SalesSummaryResponse>(
    queryKeys.salesSummary(clinicId ?? '', salesSearch),
    `/clinics/${clinicId}/sales/summary?${salesSearch}`,
    { enabled: Boolean(clinicId && branchId) && canSeeSales && chartsShown && from <= to },
  );

  const record = useApiMutation<ExpenseResponse, RecordExpenseRequest>(
    (body) => ({ path: `/clinics/${clinicId}/expenses`, options: { method: 'POST', body } }),
    {
      invalidates: [
        queryKeys.expenses(clinicId ?? '', range),
        queryKeys.expenseTotals(clinicId ?? '', range),
      ],
    },
  );

  const decide = useApiMutation<ExpenseResponse, { readonly id: string; readonly outcome: 'approve' | 'reject' }>(
    ({ id, outcome }) => ({
      path: `/clinics/${clinicId}/expenses/${id}/${outcome}`,
      options: { method: 'POST', body: {} },
    }),
    {
      invalidates: [
        queryKeys.expenses(clinicId ?? '', range),
        queryKeys.expenseTotals(clinicId ?? '', range),
      ],
    },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.expenses')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.expenses')}
        description={t('expenses.description')}
        actions={
          <button type="button" className="button button--ghost" onClick={() => showCharts(!chartsShown)}>
            {chartsShown ? t('overview.hide') : t('overview.show')}
          </button>
        }
      />

      <div className="filters">
        <label className="field">
          <span>{t('board.branch')}</span>
          <select value={branchId ?? ''} onChange={(event) => choose(event.target.value)}>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {name(branch, branch.code)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('expenses.from')}</span>
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label className="field">
          <span>{t('expenses.to')}</span>
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
      </div>

      {chartsShown && expenses.data && from <= to ? (
        <ExpensesOverview expenses={expenses.data} sales={canSeeSales ? sales.data : undefined} from={from} to={to} />
      ) : null}

      <AsyncSection query={totals}>
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('expenses.totals')} hint={t('expenses.totalsHint')} />
            {rows.length === 0 ? (
              <p className="muted">{t('expenses.none')}</p>
            ) : (
              <ul className="totals-list">
                {rows.map((row) => (
                  <li key={row.category}>
                    <span>{t(`expenses.categories.${row.category}`)}</span>
                    <strong>{money(row.total)}</strong>
                    <span className="muted">{t('expenses.count', { count: row.count ?? 0 })}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </AsyncSection>

      {canRecord ? (
        <section className="panel">
          <PanelHeading title={t('expenses.record')} />
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              record.mutate({
                branchId: branchId ?? '',
                category: String(form.get('category')) as ExpenseCategory,
                amount: { amount: Number(form.get('amount')).toFixed(2), currency: 'EGP' },
                vendor: String(form.get('vendor') ?? '') || undefined,
                description: String(form.get('description') ?? '') || undefined,
                incurredOn: String(form.get('incurredOn')),
              });
              event.currentTarget.reset();
            }}
          >
            <label className="field">
              <span>{t('expenses.category')}</span>
              <select name="category" defaultValue="CONSUMABLES">
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t(`expenses.categories.${category}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t('expenses.amount')}</span>
              <input name="amount" type="number" step="0.01" min="0.01" required />
            </label>
            <label className="field">
              <span>{t('expenses.vendor')}</span>
              <input name="vendor" />
            </label>
            <label className="field">
              <span>{t('expenses.incurredOn')}</span>
              <input name="incurredOn" type="date" defaultValue={to} required />
            </label>
            <label className="field wide">
              <span>{t('expenses.note')}</span>
              <input name="description" />
            </label>
            {record.isError ? <ErrorState error={record.error} /> : null}
            <button type="submit" className="primary" disabled={record.isPending}>
              {t('common.save')}
            </button>
          </form>
        </section>
      ) : null}

      <AsyncSection query={expenses}>
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('expenses.list')} />
            {rows.length === 0 ? (
              <p className="muted">{t('expenses.none')}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('expenses.incurredOn')}</th>
                      <th>{t('expenses.category')}</th>
                      <th>{t('expenses.vendor')}</th>
                      <th>{t('expenses.amount')}</th>
                      <th>{t('sales.status')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((expense) => (
                      <tr key={expense.id}>
                        <td>{expense.incurredOn}</td>
                        <td>{t(`expenses.categories.${expense.category}`)}</td>
                        <td>
                          {expense.vendor}
                          {expense.source !== 'MANUAL' ? (
                            <span className="chip">{t(`expenses.sources.${expense.source}`)}</span>
                          ) : null}
                        </td>
                        <td>{money(expense.amount)}</td>
                        <td>
                          <span className={`pill pill--${(expense.status ?? '').toLowerCase()}`}>
                            {t(`expenses.statuses.${expense.status}`)}
                          </span>
                        </td>
                        <td className="actions">
                          {canApprove && expense.status === 'RECORDED' && expense.source === 'MANUAL' ? (
                            <>
                              <button
                                type="button"
                                className="primary"
                                onClick={() => decide.mutate({ id: expense.id ?? '', outcome: 'approve' })}
                              >
                                {t('expenses.approve')}
                              </button>
                              <button
                                type="button"
                                onClick={() => decide.mutate({ id: expense.id ?? '', outcome: 'reject' })}
                              >
                                {t('expenses.reject')}
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </AsyncSection>
    </>
  );
}
